import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Plus, BedDouble, ShieldCheck, Star, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import DocumentVault from "../../components/DocumentVault";

const HOUSING_TYPES = [
  { value: "veteran", label: "Veteran Housing" },
  { value: "recovery", label: "Recovery / Sober Living" },
  { value: "transitional", label: "Transitional" },
  { value: "reentry", label: "Reentry" },
  { value: "ilf", label: "Independent Living" },
  { value: "family", label: "Family Housing" },
  { value: "senior", label: "Senior Housing" },
  { value: "emergency", label: "Emergency Housing" },
  { value: "shelter", label: "Shelter" },
];

const POPULATIONS = [
  { value: "veterans", label: "Veterans" },
  { value: "seniors", label: "Seniors" },
  { value: "men", label: "Men" },
  { value: "women", label: "Women" },
  { value: "couples", label: "Couples" },
  { value: "families", label: "Families" },
  { value: "recovery", label: "Recovery" },
  { value: "reentry", label: "Reentry" },
  { value: "disabled", label: "Disabled" },
  { value: "general", label: "General Population" },
];

const PROVIDER_DOC_TYPES = [
  { value: "facility_doc", label: "Facility Document" },
  { value: "organization_doc", label: "Organization Document" },
  { value: "program_doc", label: "Program Documentation" },
  { value: "contact_doc", label: "Contact Verification" },
  { value: "housing_doc", label: "Housing Document" },
  { value: "other", label: "Other" },
];

const empty = {
  name: "", housing_type: "transitional", gender: "any", veteran_only: false,
  populations_served: [],
  address: "", city: "", county: "", state: "NC", zip_code: "",
  photo_url: "", total_beds: 1, available_beds: 1, occupied_beds: 0, waitlist_count: 0,
  monthly_rent: 0, amenities: [], eligibility: "", description: "",
};

export default function ProviderDashboard() {
  const { user, refresh } = useAuth();
  const [listings, setListings] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [amenInput, setAmenInput] = useState("");

  const load = useCallback(async () => {
    const [l, r, a] = await Promise.all([
      api.get("/my/listings"),
      api.get("/referrals/received"),
      api.get("/providers/me/analytics"),
    ]);
    setListings(l.data); setReferrals(r.data); setAnalytics(a.data);
  }, []);
  useEffect(() => { load(); refresh(); }, [load, refresh]);

  const pendingReferralCount = useMemo(
    () => referrals.filter((r) => ["submitted", "under_review"].includes(r.status)).length,
    [referrals],
  );

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (l) => { setEditing(l); setForm({ ...empty, ...l }); setOpen(true); };

  const togglePop = (p) => {
    setForm({ ...form, populations_served: form.populations_served.includes(p) ? form.populations_served.filter(x => x !== p) : [...form.populations_served, p] });
  };

  const save = async () => {
    try {
      if (editing) await api.put(`/listings/${editing.id}`, form);
      else await api.post("/listings", form);
      toast.success(editing ? "Listing updated" : "Listing submitted — pending admin approval");
      setOpen(false); load();
    } catch (e) { toast.error("Could not save listing"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this listing?")) return;
    await api.delete(`/listings/${id}`);
    toast.success("Listing deleted");
    load();
  };

  const updateRef = async (id, status) => {
    await api.patch(`/referrals/${id}`, { status });
    toast.success(`Referral marked ${status.replace('_', ' ')}`);
    load();
  };

  return (
    <div data-testid="provider-dashboard" className="max-w-7xl mx-auto px-6 sm:px-8 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#C05621] font-semibold mb-2">Housing Provider</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A192F]">{user.organization || user.full_name}</h1>
          <div className="mt-2 flex flex-wrap gap-2">
            {user.verified && <Badge className="bg-[#C6F6D5] text-[#22543D] border-0"><ShieldCheck className="w-3 h-3 mr-1" /> Verified</Badge>}
            {user.subscription_tier === "preferred" && <Badge className="bg-[#FEEBC8] text-[#7B341E] border-0"><Star className="w-3 h-3 mr-1" /> Preferred</Badge>}
            {Object.entries(user.verifications || {}).filter(([_, v]) => v).map(([k]) => (
              <Badge key={k} variant="outline" className="text-[10px]">{k} ✓</Badge>
            ))}
            {!user.subscription_tier && <Link to="/pricing"><Badge variant="outline" className="cursor-pointer">Free plan — Upgrade</Badge></Link>}
          </div>
        </div>
        <Button data-testid="add-listing" onClick={openCreate} className="bg-[#1E3A8A] hover:bg-[#1A365D] text-white"><Plus className="w-4 h-4 mr-1.5" /> New Listing</Button>
      </div>

      <OnboardingBanner user={user} />

      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          <Stat title="Total Beds" value={analytics.total_beds} />
          <Stat title="Available" value={analytics.available_beds} accent />
          <Stat title="Occupied" value={analytics.occupied_beds} />
          <Stat title="Waitlist" value={analytics.waitlist_count} />
          <Stat title="Occupancy" value={`${analytics.occupancy_rate || 0}%`} />
        </div>
      )}

      <Tabs defaultValue="listings">
        <TabsList>
          <TabsTrigger value="listings" data-testid="tab-listings">My Listings</TabsTrigger>
          <TabsTrigger value="referrals" data-testid="tab-referrals">Incoming Referrals ({pendingReferralCount})</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
        </TabsList>

        <TabsContent value="listings">
          {listings.length === 0 ? (
            <div className="border border-dashed border-[#D1D5DB] p-12 text-center bg-white rounded-sm">
              <BedDouble className="w-8 h-8 mx-auto text-[#4A5568] mb-3" />
              <div className="font-heading font-semibold text-[#0A192F]">No listings yet</div>
              <div className="text-sm text-[#4A5568] mt-1">Post your first bed to start receiving referrals.</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {listings.map((l) => (
                <div key={l.id} data-testid={`provider-listing-${l.id}`} className="bg-white border border-[#D1D5DB] p-5 rounded-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-heading font-semibold text-[#0A192F]">{l.name}</div>
                      <div className="text-xs text-[#4A5568] mt-1">{l.city}{l.county && `, ${l.county} Co.`}, {l.state} · {l.housing_type.replace('_',' ')}</div>
                      {l.approval_status === "pending" && <Badge className="mt-2 bg-[#FEEBC8] text-[#7B341E]">Pending approval</Badge>}
                      {l.approval_status === "denied" && <Badge className="mt-2 bg-[#FED7D7] text-[#742A2A]">Denied — {l.denial_reason}</Badge>}
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(l)} data-testid={`edit-${l.id}`} className="p-2 hover:bg-[#F5F5F0] rounded"><Edit className="w-4 h-4 text-[#4A5568]" /></button>
                      <button onClick={() => remove(l.id)} data-testid={`del-${l.id}`} className="p-2 hover:bg-[#F5F5F0] rounded"><Trash2 className="w-4 h-4 text-[#E53E3E]" /></button>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
                    <Mini label="Available" value={l.available_beds} accent />
                    <Mini label="Occupied" value={l.occupied_beds || 0} />
                    <Mini label="Waitlist" value={l.waitlist_count || 0} />
                  </div>
                  {l.populations_served?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {l.populations_served.map(p => <Badge key={p} variant="outline" className="text-[10px] capitalize">{p}</Badge>)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="referrals">
          {referrals.length === 0 ? (
            <div className="border border-dashed border-[#D1D5DB] p-12 text-center bg-white rounded-sm text-[#4A5568] text-sm">No referrals yet.</div>
          ) : (
            <div className="space-y-3">
              {referrals.map((r) => (
                <div key={r.id} data-testid={`referral-${r.id}`} className="bg-white border border-[#D1D5DB] p-5 rounded-sm flex flex-wrap gap-4 items-start justify-between">
                  <div>
                    <div className="font-semibold text-[#0A192F]">
                      {r.client_name}
                      {r.veteran_status && <Badge variant="outline" className="ml-2 text-[10px]">Veteran</Badge>}
                      {r.verified_applicant && <Badge className="ml-2 bg-[#C6F6D5] text-[#22543D] text-[10px]">✓ Verified Applicant</Badge>}
                    </div>
                    <div className="text-xs text-[#4A5568] mt-0.5">For {r.listing_name} · From {r.case_manager_name || "self-applied"}</div>
                    {r.client_phone && <div className="text-xs text-[#4A5568]">📞 {r.client_phone}</div>}
                    {r.notes && <div className="text-sm text-[#4A5568] mt-2 max-w-xl">{r.notes}</div>}
                    {r.applicant_verifications && Object.keys(r.applicant_verifications).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {Object.entries(r.applicant_verifications).filter(([_, v]) => v).map(([k]) => (
                          <Badge key={k} variant="outline" className="text-[9px] capitalize">{k} ✓</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 items-center flex-wrap">
                    <Badge className={statusColor(r.status)}>{r.status.replace('_', ' ')}</Badge>
                    {nextActions(r.status).map((a) => (
                      <Button key={a.status} size="sm" data-testid={`act-${a.status}-${r.id}`} onClick={() => updateRef(r.id, a.status)} className={a.className}>{a.label}</Button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="documents">
          <DocumentVault
            title="Provider Documents"
            description="Upload facility licenses, organization documents, program details, and contact verification documents. Admins review for the Verified Provider badge."
            docTypes={PROVIDER_DOC_TYPES}
            defaultLinkedTo="provider_verification"
            testId="provider-document-vault"
          />
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit listing" : "New listing"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2"><Label>Property name</Label><Input data-testid="lst-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Type</Label>
              <Select value={form.housing_type} onValueChange={(v) => setForm({ ...form, housing_type: v })}>
                <SelectTrigger data-testid="lst-type"><SelectValue /></SelectTrigger>
                <SelectContent>{HOUSING_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Gender</Label>
              <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                <SelectTrigger data-testid="lst-gender"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Address</Label><Input data-testid="lst-addr" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div><Label>City</Label><Input data-testid="lst-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div><Label>County</Label><Input data-testid="lst-county" value={form.county} onChange={(e) => setForm({ ...form, county: e.target.value })} placeholder="Mecklenburg" /></div>
            <div><Label>State</Label><Input data-testid="lst-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} maxLength={2} /></div>
            <div><Label>ZIP</Label><Input data-testid="lst-zip" value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} /></div>
            <div><Label>Total beds</Label><Input data-testid="lst-total" type="number" min={1} value={form.total_beds} onChange={(e) => setForm({ ...form, total_beds: parseInt(e.target.value || "0") })} /></div>
            <div><Label>Available beds</Label><Input data-testid="lst-avail" type="number" min={0} value={form.available_beds} onChange={(e) => setForm({ ...form, available_beds: parseInt(e.target.value || "0") })} /></div>
            <div><Label>Occupied beds</Label><Input data-testid="lst-occupied" type="number" min={0} value={form.occupied_beds} onChange={(e) => setForm({ ...form, occupied_beds: parseInt(e.target.value || "0") })} /></div>
            <div><Label>Waitlist count</Label><Input data-testid="lst-waitlist" type="number" min={0} value={form.waitlist_count} onChange={(e) => setForm({ ...form, waitlist_count: parseInt(e.target.value || "0") })} /></div>
            <div><Label>Monthly rent ($)</Label><Input data-testid="lst-rent" type="number" min={0} value={form.monthly_rent} onChange={(e) => setForm({ ...form, monthly_rent: parseFloat(e.target.value || "0") })} /></div>
            <div><Label>Photo URL</Label><Input data-testid="lst-photo" value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} placeholder="https://…" /></div>

            <div className="md:col-span-2">
              <Label>Populations served</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 mt-2">
                {POPULATIONS.map(p => (
                  <label key={p.value} className="flex items-center gap-2 text-xs">
                    <Checkbox data-testid={`pop-${p.value}`} checked={form.populations_served.includes(p.value)} onCheckedChange={() => togglePop(p.value)} />
                    {p.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="md:col-span-2 flex items-center gap-2 text-sm"><Checkbox data-testid="lst-vet" checked={form.veteran_only} onCheckedChange={(v) => setForm({ ...form, veteran_only: !!v })} /> Veteran-only facility</div>
            <div className="md:col-span-2">
              <Label>Amenities (press Enter)</Label>
              <Input value={amenInput} onChange={(e) => setAmenInput(e.target.value)} data-testid="lst-amen-input"
                onKeyDown={(e) => { if (e.key === "Enter" && amenInput.trim()) { e.preventDefault(); setForm({ ...form, amenities: [...form.amenities, amenInput.trim()] }); setAmenInput(""); } }} />
              <div className="flex flex-wrap gap-1.5 mt-2">{form.amenities.map((a, i) => <Badge key={`amen-${a}-${i}`} variant="outline" className="cursor-pointer" onClick={() => setForm({ ...form, amenities: form.amenities.filter((_, idx) => idx !== i) })}>{a} ×</Badge>)}</div>
            </div>
            <div className="md:col-span-2"><Label>Eligibility</Label><Input data-testid="lst-elig" value={form.eligibility} onChange={(e) => setForm({ ...form, eligibility: e.target.value })} placeholder="e.g. Veterans 18+, sober 30 days" /></div>
            <div className="md:col-span-2"><Label>Description</Label><Textarea data-testid="lst-desc" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button data-testid="lst-save" onClick={save} className="bg-[#1E3A8A] hover:bg-[#1A365D] text-white">{editing ? "Update" : "Submit for Approval"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ title, value, accent }) => (
  <div className="bg-white border border-[#D1D5DB] p-4 rounded-sm">
    <div className="text-xs uppercase tracking-widest text-[#4A5568]">{title}</div>
    <div className={`font-heading text-3xl font-bold mt-1 ${accent ? "text-[#C05621]" : "text-[#0A192F]"}`}>{value}</div>
  </div>
);
const Mini = ({ label, value, accent }) => (
  <div className={`px-3 py-2 border ${accent ? "border-[#C05621] bg-[#FEF7F0]" : "border-[#D1D5DB] bg-[#F5F5F0]"} rounded-sm text-center`}>
    <div className="text-[10px] uppercase tracking-wider text-[#4A5568]">{label}</div>
    <div className={`font-heading font-bold ${accent ? "text-[#C05621]" : "text-[#0A192F]"}`}>{value}</div>
  </div>
);

const statusColor = (s) => ({
  submitted: "bg-[#FEEBC8] text-[#7B341E]",
  under_review: "bg-[#BEE3F8] text-[#2A4365]",
  accepted: "bg-[#E9D8FD] text-[#44337A]",
  waitlisted: "bg-[#FAF089] text-[#744210]",
  placed: "bg-[#C6F6D5] text-[#22543D]",
  completed: "bg-[#276749] text-white",
  closed: "bg-[#1A202C] text-white",
  rejected: "bg-[#FED7D7] text-[#742A2A]",
}[s] || "bg-[#F5F5F0] text-[#0A192F]");

const nextActions = (status) => {
  const review = { status: "under_review", label: "Review", className: "bg-[#1E3A8A] hover:bg-[#1A365D]" };
  const accept = { status: "accepted", label: "Accept", className: "bg-[#1E3A8A] hover:bg-[#1A365D]" };
  const waitlist = { status: "waitlisted", label: "Waitlist", className: "bg-[#D69E2E] hover:bg-[#B7791F]" };
  const place = { status: "placed", label: "Mark Placed", className: "bg-[#276749] hover:bg-[#22543D]" };
  const complete = { status: "completed", label: "Mark Completed", className: "bg-[#0A192F] hover:bg-[#1A365D]" };
  const close = { status: "closed", label: "Close", className: "bg-white text-[#1A202C] border border-[#1A202C] hover:bg-[#F5F5F0]" };
  const reject = { status: "rejected", label: "Reject", className: "bg-white text-[#742A2A] border border-[#742A2A] hover:bg-[#FED7D7]" };
  if (status === "submitted") return [review, reject];
  if (status === "under_review") return [accept, waitlist, reject];
  if (status === "waitlisted") return [accept, close];
  if (status === "accepted") return [place, close];
  if (status === "placed") return [complete, close];
  return [];
};


const OnboardingBanner = ({ user }) => {
  const status = user.application_status;
  if (status === "approved") return null;
  // Pre-existing providers (no application_status) get the grandfathered banner
  const isGrandfathered = !status || status === "grandfathered";
  if (isGrandfathered) {
    return (
      <div data-testid="onboarding-banner-grandfathered" className="bg-[#FEF6E4] border-l-4 border-[#C05621] rounded-sm p-4 mb-6 flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className="font-heading text-base font-semibold text-[#0A192F]">Complete your Provider Application</div>
          <p className="text-sm text-[#4A5568] mt-1">Your account is grandfathered in and your existing listings remain active. To earn a verified status badge and increase visibility, complete the new Provider Application.</p>
        </div>
        <Link to="/provider/application" data-testid="cta-complete-application">
          <Button className="bg-[#C05621] hover:bg-[#9C4221] text-white">Complete Application</Button>
        </Link>
      </div>
    );
  }
  if (status === "not_started" || status === "draft") {
    return (
      <div data-testid="onboarding-banner-incomplete" className="bg-[#FED7D7] border-l-4 border-[#C05621] rounded-sm p-4 mb-6 flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className="font-heading text-base font-semibold text-[#0A192F]">Provider Application required before publishing listings</div>
          <p className="text-sm text-[#4A5568] mt-1">You must complete the application and upload required documents (Proof of Organization, Licensing, Insurance) before submitting listings for approval.</p>
        </div>
        <Link to="/provider/application" data-testid="cta-continue-application">
          <Button className="bg-[#C05621] hover:bg-[#9C4221] text-white">{status === "draft" ? "Continue Application" : "Start Application"}</Button>
        </Link>
      </div>
    );
  }
  if (status === "submitted" || status === "under_review") {
    return (
      <div data-testid="onboarding-banner-review" className="bg-[#BEE3F8] border-l-4 border-[#2C5282] rounded-sm p-4 mb-6">
        <div className="font-heading text-base font-semibold text-[#0A192F]">Application under review</div>
        <p className="text-sm text-[#4A5568] mt-1">Your application has been submitted and is awaiting admin review. You will receive an email notification when a decision is made.</p>
      </div>
    );
  }
  if (status === "denied") {
    return (
      <div data-testid="onboarding-banner-denied" className="bg-[#FED7D7] border-l-4 border-[#C05621] rounded-sm p-4 mb-6 flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className="font-heading text-base font-semibold text-[#0A192F]">Application denied — please update and resubmit</div>
          <p className="text-sm text-[#4A5568] mt-1">An administrator denied your application. Review the feedback and resubmit a new version.</p>
        </div>
        <Link to="/provider/application">
          <Button className="bg-[#C05621] hover:bg-[#9C4221] text-white">Review & Resubmit</Button>
        </Link>
      </div>
    );
  }
  return null;
};
